package fr.frux.feature.treedetail.move

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.maps.Location
import fr.frux.shared.content.TreeCorrectionLimits
import fr.frux.shared.domain.model.TreeCorrection
import fr.frux.shared.domain.repository.TreeCorrectionRepository
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

/** Sorties de l'écran de déplacement, toutes rendues à l'hôte qui dépile. */
sealed interface MoveTreeEvent {
    data object Saved : MoveTreeEvent

    data object ReportedGone : MoveTreeEvent

    data object Cancelled : MoveTreeEvent
}

data class MoveTreeState(
    val saving: Boolean = false,
    val reporting: Boolean = false,
    val error: AppError? = null,
)

/**
 * Déplacement d'un arbre, borné à [TreeCorrectionLimits.MAX_MOVE_METERS] de sa position actuelle.
 * Au-delà, ce n'est plus une correction : l'écran propose d'ajouter un autre arbre ou de signaler
 * la disparition, et [save] ne part pas.
 *
 * La [idempotencyKey] est tirée à l'ouverture de l'écran et gardée pour chaque nouvel essai, de
 * sorte qu'un envoi rejoué après une coupure n'écrive pas deux révisions.
 */
class MoveTreeViewModel(
    private val args: MoveTreeArgs,
    private val corrections: TreeCorrectionRepository,
    private val treeRepository: TreeRepository,
    private val idempotencyKey: String = Uuid.random().toString(),
) : ViewModel() {
    private val _state = MutableStateFlow(MoveTreeState())
    val state: StateFlow<MoveTreeState> = _state.asStateFlow()

    private val _events = Channel<MoveTreeEvent>(Channel.BUFFERED)
    val events: Flow<MoveTreeEvent> = _events.receiveAsFlow()

    private val busy: Boolean get() = _state.value.saving || _state.value.reporting

    fun save(target: Location) {
        val distance =
            TreeCorrectionLimits.distanceMeters(args.latitude, args.longitude, target.latitude, target.longitude)
        val movable =
            distance >= TreeCorrectionLimits.POSITION_NOISE_METERS && distance <= TreeCorrectionLimits.MAX_MOVE_METERS
        if (!movable || busy) return
        _state.update { it.copy(saving = true, error = null) }
        viewModelScope.launch {
            corrections
                .correct(
                    treeId = args.treeId,
                    correction = TreeCorrection.Position(target.latitude, target.longitude),
                    undo = TreeCorrection.Position(args.latitude, args.longitude),
                    idempotencyKey = idempotencyKey,
                ).fold(
                    onSuccess = {
                        _state.update { it.copy(saving = false) }
                        _events.send(MoveTreeEvent.Saved)
                    },
                    onFailure = { error -> _state.update { it.copy(saving = false, error = error.asAppError()) } },
                )
        }
    }

    fun reportGone() {
        if (!args.canReportGone || busy) return
        _state.update { it.copy(reporting = true, error = null) }
        viewModelScope.launch {
            treeRepository.reportTree(args.treeId, kind = REPORT_KIND_GONE).fold(
                onSuccess = {
                    _state.update { it.copy(reporting = false) }
                    _events.send(MoveTreeEvent.ReportedGone)
                },
                onFailure = { error -> _state.update { it.copy(reporting = false, error = error.asAppError()) } },
            )
        }
    }

    fun cancel() {
        _events.trySend(MoveTreeEvent.Cancelled)
    }

    fun consumeError() {
        _state.update { it.copy(error = null) }
    }

    private companion object {
        const val REPORT_KIND_GONE = "GONE"
    }
}
